/**
 * ConfigNotificaciones — editor de plantillas WhatsApp transaccionales.
 *
 * Una plantilla por evento (confirmacion, reagendamiento, suspension_clima,
 * cancelacion). Editor con textarea de cuerpo, chips de variables disponibles
 * (clic para insertarlas), preview live y botón "Enviar prueba".
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Chip, CircularProgress, Switch,
  Tabs, Tab, Paper, Stack, Alert, IconButton, Tooltip, Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import useToast from './common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const getEmpresaId = () => {
  try {
    const ea = localStorage.getItem('empresa_activa');
    if (ea) return ea;
    return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1';
  } catch { return '1'; }
};
const getAuthHeaders = () => {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const EVENTOS_ORDEN = ['confirmacion', 'reagendamiento', 'suspension_clima', 'cancelacion'];

const EVENTO_META = {
  confirmacion:     { color: '#16a34a', icon: '✅' },
  reagendamiento:   { color: '#f59e0b', icon: '📅' },
  suspension_clima: { color: '#dc2626', icon: '⚠️' },
  cancelacion:      { color: '#64748b', icon: '❌' },
};

export default function ConfigNotificaciones() {
  const empresaId = getEmpresaId();
  const { toast, ToastContainer } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [tab, setTab] = useState(0);
  const [editado, setEditado] = useState({});  // { plantillaId: { cuerpo, activa } }
  const [preview, setPreview] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testDialogNumero, setTestDialogNumero] = useState('');
  const [enviandoTest, setEnviandoTest] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/notificaciones/plantillas?empresa_id=${empresaId}`,
        { headers: getAuthHeaders() });
      if (!r.ok) throw new Error('No se pudieron cargar las plantillas');
      const d = await r.json();
      // Ordenar según EVENTOS_ORDEN
      const ordenadas = [...(d.plantillas || [])].sort(
        (a, b) => EVENTOS_ORDEN.indexOf(a.evento) - EVENTOS_ORDEN.indexOf(b.evento)
      );
      setPlantillas(ordenadas);
      setEditado({});
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  const plantillaActiva = plantillas[tab];
  const valorEditado = plantillaActiva && editado[plantillaActiva.id]
    ? editado[plantillaActiva.id]
    : { cuerpo: plantillaActiva?.cuerpo || '', activa: plantillaActiva?.activa ?? true };
  const dirty = plantillaActiva && editado[plantillaActiva.id] !== undefined;

  // Cargar preview al cambiar plantilla o cuerpo
  useEffect(() => {
    if (!plantillaActiva) return;
    let cancel = false;
    setPreviewLoading(true);
    fetch(`${API_BASE}/turismo/notificaciones/preview`, {
      method: 'POST', headers: getAuthHeaders(),
      body: JSON.stringify({
        empresa_id: empresaId,
        evento: plantillaActiva.evento,
        idioma: plantillaActiva.idioma || 'es',
      }),
    })
      .then(r => r.json())
      .then(d => {
        if (cancel) return;
        // Si hay edición pendiente, usar el cuerpo editado y reemplazar variables localmente
        if (dirty) {
          let texto = valorEditado.cuerpo;
          for (const [k, v] of Object.entries(d.variables || {})) {
            texto = texto.split(`{${k}}`).join(v);
          }
          texto = texto.replace(/\{[a-z_][a-z0-9_]*\}/g, '');
          setPreview(texto);
        } else {
          setPreview(d.mensaje || '');
        }
      })
      .catch(() => { if (!cancel) setPreview(''); })
      .finally(() => { if (!cancel) setPreviewLoading(false); });
    return () => { cancel = true; };
  }, [plantillaActiva?.id, valorEditado.cuerpo, dirty, empresaId]); // eslint-disable-line

  const handleEditar = (campo, valor) => {
    if (!plantillaActiva) return;
    setEditado(prev => ({
      ...prev,
      [plantillaActiva.id]: { ...valorEditado, [campo]: valor },
    }));
  };

  const handleGuardar = async () => {
    if (!plantillaActiva || !dirty) return;
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const body = {
        cuerpo: valorEditado.cuerpo,
        activa: valorEditado.activa,
        user_id: user.id || null,
        user_nombre: user.nombre || user.email || null,
      };
      const r = await fetch(`${API_BASE}/turismo/notificaciones/plantillas/${plantillaActiva.id}`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Error guardando');
      }
      toast.success('Plantilla actualizada');
      await cargar();
      // Volver al mismo tab tras recargar
      setTab(t => Math.min(t, plantillas.length - 1));
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDescartar = () => {
    if (!plantillaActiva) return;
    setEditado(prev => {
      const n = { ...prev };
      delete n[plantillaActiva.id];
      return n;
    });
  };

  const handleInsertarVariable = (variable) => {
    const placeholder = `{${variable}}`;
    handleEditar('cuerpo', (valorEditado.cuerpo || '') + placeholder);
  };

  const handleEnviarTest = async () => {
    if (!testDialogNumero || !plantillaActiva) return;
    setEnviandoTest(true);
    try {
      // Si hay edición sin guardar, primero guardamos
      if (dirty) await handleGuardar();
      const r = await fetch(`${API_BASE}/turismo/notificaciones/enviar-test`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          empresa_id: empresaId,
          evento: plantillaActiva.evento,
          numero_destino: testDialogNumero,
          idioma: plantillaActiva.idioma || 'es',
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Error enviando');
      }
      toast.success(`Mensaje de prueba enviado a ${testDialogNumero}`);
    } catch (e) { toast.error(e.message); }
    setEnviandoTest(false);
  };

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={32} /></Box>;
  }
  if (!plantillas.length) {
    return <Alert severity="info">No hay plantillas configuradas para esta empresa.</Alert>;
  }

  return (
    <Box>
      <Alert severity="info" icon={<WhatsAppIcon />} sx={{ mb: 2, fontSize: 13 }}>
        Estos son los mensajes que se envían automáticamente por WhatsApp cuando ocurren
        eventos en una reserva. Usa <code>{'{variable}'}</code> para insertar datos del turista.
      </Alert>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: '1px solid #e2e8f0',
              '& .MuiTabs-indicator': { backgroundColor: '#0d9488' } }}>
        {plantillas.map(p => {
          const meta = EVENTO_META[p.evento] || {};
          return (
            <Tab key={p.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <span>{meta.icon}</span>
                  <span>{p.titulo}</span>
                  {!p.activa && <Chip size="small" label="inactiva" sx={{ height: 16, fontSize: 9 }} />}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 13, minHeight: 44 }}
            />
          );
        })}
      </Tabs>

      {plantillaActiva && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {/* Editor */}
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
                Editor
              </Typography>
              <Tooltip title={valorEditado.activa ? 'Activa' : 'Inactiva — no se envía'}>
                <Switch
                  size="small"
                  checked={!!valorEditado.activa}
                  onChange={e => handleEditar('activa', e.target.checked)}
                />
              </Tooltip>
            </Stack>

            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1 }}>
              {plantillaActiva.descripcion}
            </Typography>

            <TextField
              multiline rows={12} fullWidth
              value={valorEditado.cuerpo}
              onChange={e => handleEditar('cuerpo', e.target.value)}
              placeholder="Escribe el mensaje..."
              sx={{ '& .MuiInputBase-input': { fontSize: 13, fontFamily: 'monospace' } }}
            />

            <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 1.5, mb: 0.5,
                              textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
              Variables disponibles (clic para insertar)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(plantillaActiva.variables_disponibles || []).map(v => (
                <Chip key={v} size="small" label={`{${v}}`}
                  onClick={() => handleInsertarVariable(v)}
                  sx={{
                    fontSize: 10, fontFamily: 'monospace', cursor: 'pointer',
                    bgcolor: '#f1f5f9', color: '#0d9488',
                    '&:hover': { bgcolor: '#ccfbf1' },
                  }} />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1}>
              <Button startIcon={<SaveIcon />} variant="contained"
                disabled={!dirty || saving} onClick={handleGuardar} disableElevation
                sx={{ textTransform: 'none',
                      background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
                Guardar
              </Button>
              {dirty && (
                <Button onClick={handleDescartar} sx={{ textTransform: 'none' }}>
                  Descartar
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              {plantillaActiva.updated_at && (
                <Typography sx={{ fontSize: 10, color: '#94a3b8', alignSelf: 'center' }}>
                  Editada: {new Date(plantillaActiva.updated_at).toLocaleString('es-CO')}
                </Typography>
              )}
            </Stack>
          </Paper>

          {/* Preview */}
          <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f0fdf4' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <VisibilityIcon sx={{ color: '#16a34a', fontSize: 18 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#166534', flex: 1 }}>
                Vista previa (datos de ejemplo)
              </Typography>
              <IconButton size="small" onClick={cargar}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>

            <Box sx={{
              bgcolor: '#fff', border: '1px solid #bbf7d0', borderRadius: 2,
              p: 1.5, minHeight: 260,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: 13, lineHeight: 1.55, color: '#0f172a',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {previewLoading
                ? <CircularProgress size={20} sx={{ color: '#16a34a' }} />
                : (preview || <em style={{ color: '#94a3b8' }}>Sin contenido para previsualizar</em>)}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1, fontWeight: 700 }}>
              Enviar a un número de prueba
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" fullWidth placeholder="+573001234567"
                value={testDialogNumero}
                onChange={e => setTestDialogNumero(e.target.value)} />
              <Button variant="contained" startIcon={<SendIcon />} disableElevation
                disabled={!testDialogNumero || enviandoTest} onClick={handleEnviarTest}
                sx={{ textTransform: 'none', background: '#25D366',
                      '&:hover': { background: '#128C7E' }, whiteSpace: 'nowrap' }}>
                {enviandoTest ? 'Enviando…' : 'Enviar'}
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      <ToastContainer />
    </Box>
  );
}
